import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ChevronRight, Delete, Edit, KeyboardArrowDown, Search } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DataTable } from "@/components/DataTable";
import { FilterModal } from "@/components/FilterModal";
import { CrudModal } from "@/components/crud/CrudModal";
import usePagination from "@/hooks/pagination";
import { useCrud } from "@/hooks/useCrud";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import { getUserCompanies } from "@/api/user.service";
import {
  CATEGORIA_TODAS,
  deleteEvento,
  listCategorias,
  listEventos,
  localizarCategorias,
  saveEvento,
  type CategoriaMercadologica,
  type Efeito,
  type Evento as EventoApi,
  type TipoEvento,
} from "@/api/metas.service";
import {
  chaveDe,
  mesesPermitidos,
  normalizar,
  paraEscala,
  partesDe,
  rotuloMesChave,
} from "./metasFormato";

// Tela de apoio ao cálculo das metas: eventos (feriados/sazonalidades) que
// influenciam a meta-base por período, loja e categoria. Os eventos são
// persistidos pelo Product Runtime (metas.list_eventos / save_evento /
// delete_evento), as lojas são as empresas que o usuário logado enxerga no
// cliente atual e a hierarquia de mercadologia vem do ERP pela consulta do
// próprio módulo (metas.mercadologia), carregada nível a nível.

/** Uma loja elegível para o evento: o código da empresa no ERP e seu rótulo. */
interface Loja {
  id: string;
  nome: string;
}

/** O que se sabe de cada nó já carregado da mercadologia. */
type Indice = Record<string, { nome: string; folha: boolean; pais: string[] }>;

/** A árvore do ERP como a tela a consome: carregada sob demanda, um nível por vez. */
interface Mercadologia {
  raiz: CategoriaMercadologica[];
  filhos: Record<string, CategoriaMercadologica[]>;
  carregandoRaiz: boolean;
  carregandoFilhos: Record<string, boolean>;
  indice: Indice;
  carregarFilhos: (id: string) => void;
  /** Resolve ids salvos que a árvore ainda não carregou — categoria a alguns níveis do topo, indexada direto pelo código (ver `localizarCategorias`). */
  resolverCategorias: (ids: string[]) => void;
}

/** Chave usada quando o evento vale para toda a árvore. */
const TODAS = CATEGORIA_TODAS;

/** Regra como o formulário a manipula: o percentual é o texto do campo. */
interface Regra {
  efeito: Efeito;
  percentual: string;
  observacoes: string;
}

/**
 * Evento como a tela trabalha: as regras ficam indexadas pela categoria (o
 * back-end as troca como lista) e `categorias` guarda a seleção da hierarquia.
 */
interface Evento {
  id: string;
  nome: string;
  tipo: TipoEvento;
  ativo: boolean;
  dataInicial: string;
  dataFinal: string;
  repetirAnualmente: boolean;
  abrangencia: "global" | "especificas";
  lojas: string[];
  escopoCategorias: "todas" | "especificas";
  categorias: string[];
  regras: Record<string, Regra>;
  /** Além do intervalo de datas (sempre presente), o evento pode também valer em meses anteriores específicos. */
  usarMesesAnteriores: boolean;
  /** Peso (texto do campo) de cada mês selecionado, indexado por "AAAA-MM" — juntos têm que somar 100%; só usado quando usarMesesAnteriores é true. */
  periodosAnteriores: Record<string, string>;
}

type Rascunho = Omit<Evento, "id">;

const REGRA_PADRAO: Regra = { efeito: "referencia", percentual: "0", observacoes: "" };

const RASCUNHO_VAZIO: Rascunho = {
  nome: "",
  tipo: "Sazonalidade",
  ativo: true,
  dataInicial: "",
  dataFinal: "",
  repetirAnualmente: false,
  abrangencia: "global",
  lojas: [],
  escopoCategorias: "todas",
  categorias: [],
  regras: {},
  usarMesesAnteriores: false,
  periodosAnteriores: {},
};

/** Converte o evento gravado no back-end para o formato do formulário. */
function daApi(evento: EventoApi): Evento {
  const categorias = evento.escopoCategorias === "especificas" ? evento.regras.map((r) => r.categoriaId) : [];
  return {
    id: evento.id ?? "",
    nome: evento.nome,
    tipo: evento.tipo,
    ativo: evento.ativo,
    dataInicial: evento.dataInicial,
    dataFinal: evento.dataFinal,
    repetirAnualmente: evento.repetirAnualmente,
    abrangencia: evento.abrangencia,
    lojas: evento.lojas,
    escopoCategorias: evento.escopoCategorias,
    categorias,
    regras: Object.fromEntries(
      evento.regras.map((r) => [
        r.categoriaId,
        { efeito: r.efeito, percentual: String(r.percentual), observacoes: r.observacoes },
      ]),
    ),
    usarMesesAnteriores: evento.periodos.length > 0,
    periodosAnteriores: Object.fromEntries(
      evento.periodos.map((p) => [chaveDe(p.ano, p.mes), String(p.percentual)]),
    ),
  };
}

/** Converte periodosAnteriores (texto do formulário) para o formato do back-end. */
function periodosDe(periodosAnteriores: Record<string, string>) {
  return Object.entries(periodosAnteriores).map(([chave, percentual]) => {
    const { ano, mes } = partesDe(chave);
    return { ano, mes, percentual: Number(String(percentual).replace(",", ".")) };
  });
}

/**
 * Converte o formulário para o formato do back-end: só as regras dos ids em
 * escopo são enviadas (as demais ficariam órfãs) e um evento global não leva
 * lojas.
 */
function paraApi(rascunho: Rascunho, id?: string): EventoApi {
  return {
    ...(id ? { id } : {}),
    nome: rascunho.nome.trim(),
    tipo: rascunho.tipo,
    ativo: rascunho.ativo,
    dataInicial: rascunho.dataInicial,
    dataFinal: rascunho.dataFinal,
    repetirAnualmente: rascunho.repetirAnualmente,
    abrangencia: rascunho.abrangencia,
    lojas: rascunho.abrangencia === "global" ? [] : rascunho.lojas,
    escopoCategorias: rascunho.escopoCategorias,
    regras: idsComRegra(rascunho).map((categoriaId) => {
      const regra = regraDe(rascunho, categoriaId);
      return {
        categoriaId,
        efeito: regra.efeito,
        percentual: regra.efeito === "referencia" ? 0 : Number(String(regra.percentual).replace(",", ".")),
        observacoes: regra.observacoes,
      };
    }),
    periodos: rascunho.usarMesesAnteriores ? periodosDe(rascunho.periodosAnteriores) : [],
  };
}

function caminhoDe(indice: Indice, pais: string[]) {
  return pais.map((p) => indice[p]?.nome ?? p).join(" › ");
}

function formatarData(iso: string) {
  return iso ? iso.split("-").reverse().join("/") : "—";
}

/** Ancestrais de um nó; vazio para um id cujo nível ainda não foi carregado. */
function ancestraisDe(indice: Indice, id: string) {
  return indice[id]?.pais ?? [];
}

// Um id gravado pode ainda não ter sido carregado do ERP (a árvore vem nível a
// nível) ou ter saído da mercadologia: nesse caso o próprio código é o rótulo,
// em vez de a regra sumir da tela.
function nomeCategoria(indice: Indice, id: string) {
  if (id === TODAS) return "Todas as categorias e produtos";
  return indice[id]?.nome ?? id;
}

function rotuloEfeito(regra: Regra) {
  if (regra.efeito === "referencia") return "Somente referência";
  const n = Number(regra.percentual);
  const fator = (1 + n / 100).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
  return `${n > 0 ? "+" : ""}${n.toLocaleString("pt-BR")}% · fator ${fator}`;
}

function rotuloPeriodo(e: Rascunho) {
  const intervalo = `${formatarData(e.dataInicial)} a ${formatarData(e.dataFinal)}`;
  const base = e.repetirAnualmente ? `${intervalo} · anual` : intervalo;
  if (!e.usarMesesAnteriores) return base;
  const n = Object.keys(e.periodosAnteriores).length;
  return `${base} + ${n} ${n === 1 ? "mês anterior" : "meses anteriores"}`;
}

// Uma loja gravada pode não estar mais entre as empresas do usuário; o código
// segue aparecendo para que a abrangência não suma da listagem.
function nomesLojas(e: Rascunho, lojas: Loja[]) {
  return e.lojas.map((id) => lojas.find((l) => l.id === id)?.nome ?? id);
}

function rotuloLojas(e: Rascunho, lojas: Loja[]) {
  if (e.abrangencia === "global") return "Todas as lojas";
  return nomesLojas(e, lojas).join(", ") || "Nenhuma loja selecionada";
}

/** Ids que recebem regra própria: a árvore inteira ou cada seleção da hierarquia. */
function idsComRegra(e: Rascunho) {
  return e.escopoCategorias === "todas" ? [TODAS] : e.categorias;
}

function regraDe(e: Rascunho, id: string) {
  return e.regras[id] ?? REGRA_PADRAO;
}

/** Resumo de uma linha da listagem: com várias regras o detalhe fica no modal. */
function rotuloInfluencia(e: Rascunho, indice: Indice) {
  const ids = idsComRegra(e);
  if (ids.length === 0) return "Nenhuma regra definida";
  if (ids.length === 1) return `${nomeCategoria(indice, ids[0]!)}: ${rotuloEfeito(regraDe(e, ids[0]!))}`;
  return `${ids.length} categorias com regra`;
}

/** Mesma faixa que o back-end aceita para qualquer percentual do evento (regra ou período): -100 a 10000, até duas casas. */
function percentualValido(texto: string) {
  const normalizado = texto.trim().replace(",", ".");
  const n = Number(normalizado);
  return /^-?\d+(\.\d{1,2})?$/.test(normalizado) && n >= -100 && n <= 10000;
}

/** Peso de um mês anterior: duas casas decimais, sempre positivo — mesma regra dos meses de referência de Cenário (MetasPeriodosTab.tsx), só que numa escala de 2 casas em vez de 3. */
function centesimos(valor: string) {
  return paraEscala(valor, 2);
}

/** Soma os pesos dos meses anteriores selecionados: igual à regra dos meses de referência de Cenário — precisa somar exatamente 100%, sem limite de quantos meses. */
function totalMesesAnteriores(periodosAnteriores: Record<string, string>) {
  const valores = Object.values(periodosAnteriores);
  const soma = valores.reduce((acc, v) => acc + (centesimos(v) ?? 0), 0);
  const validos = valores.every((v) => {
    const c = centesimos(v);
    return c !== null && c > 0 && c <= 10000;
  });
  return { soma, validos, quantidade: valores.length, ok: valores.length > 0 && validos && soma === 10000 };
}

function formatarCentesimos(valor: number) {
  return (valor / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function validar(e: Rascunho, indice: Indice) {
  const problemas: string[] = [];
  if (!e.nome.trim()) problemas.push("Informe o nome do evento.");
  if (!e.dataInicial || !e.dataFinal) problemas.push("Informe a data inicial e a data final.");
  else if (e.dataFinal < e.dataInicial) problemas.push("A data final deve ser igual ou posterior à inicial.");
  if (e.repetirAnualmente && e.dataInicial.slice(0, 4) !== e.dataFinal.slice(0, 4))
    problemas.push("Para repetição anual, cadastre períodos dentro do mesmo ano.");
  if (e.usarMesesAnteriores) {
    const total = totalMesesAnteriores(e.periodosAnteriores);
    if (!total.quantidade) problemas.push("Selecione ao menos um mês anterior.");
    else if (!total.validos)
      problemas.push("Informe um peso maior que zero, com até duas casas decimais, para cada mês anterior selecionado.");
    else if (total.soma !== 10000)
      problemas.push(
        `A soma dos pesos dos meses anteriores deve ser exatamente 100%. Total informado: ${formatarCentesimos(total.soma)}%.`,
      );
  }
  if (e.abrangencia === "especificas" && e.lojas.length === 0) problemas.push("Selecione pelo menos uma loja.");
  if (e.escopoCategorias === "especificas" && e.categorias.length === 0)
    problemas.push("Selecione pelo menos uma categoria ou produto.");
  idsComRegra(e).forEach((id) => {
    const regra = regraDe(e, id);
    if (regra.efeito !== "percentual") return;
    if (!percentualValido(String(regra.percentual)))
      problemas.push(
        `Informe um percentual entre -100 e 10000, com até duas casas, para ${nomeCategoria(indice, id)}.`,
      );
  });
  return problemas;
}

interface LinhaRegraProps {
  nome: string;
  regra: Regra;
  habilitada: boolean;
  herdada: boolean;
  onChange: (patch: Partial<Regra>) => void;
}

function CelulasRegra({ nome, regra, habilitada, herdada, onChange }: LinhaRegraProps) {
  return (
    <>
      <TableCell sx={{ minWidth: 190 }}>
        <TextField
          select
          size="small"
          fullWidth
          disabled={!habilitada}
          label="Aplicação no cálculo"
          slotProps={{ inputLabel: { shrink: true } }}
          value={regra.efeito}
          onChange={(ev) => onChange({ efeito: ev.target.value as Efeito })}
          aria-label={`Aplicação no cálculo — ${nome}`}
        >
          <MenuItem value="referencia">Somente referência</MenuItem>
          <MenuItem value="percentual">Ajuste percentual</MenuItem>
        </TextField>
      </TableCell>
      <TableCell sx={{ minWidth: 130 }}>
        <TextField
          type="text"
          inputMode="decimal"
          size="small"
          fullWidth
          label="Variação (%)"
          slotProps={{ inputLabel: { shrink: true } }}
          disabled={!habilitada || regra.efeito === "referencia"}
          value={habilitada ? regra.percentual : ""}
          placeholder="—"
          onChange={(ev) => {
            const valor = ev.target.value;
            if (!/^-?\d*[.,]?\d{0,2}$/.test(valor)) return;
            onChange({ percentual: valor });
          }}
          aria-label={`Variação percentual — ${nome}`}
        />
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={3}
          label="Observações"
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 500 } }}
          disabled={!habilitada}
          value={habilitada ? regra.observacoes : ""}
          placeholder={
            herdada ? "Usa a regra da categoria superior" : !habilitada ? "Selecione a categoria" : "Observações desta categoria"
          }
          onChange={(ev) => onChange({ observacoes: ev.target.value })}
          aria-label={`Observações — ${nome}`}
        />
      </TableCell>
    </>
  );
}

interface MesesAnterioresProps {
  periodosAnteriores: Record<string, string>;
  onChange: (periodosAnteriores: Record<string, string>) => void;
}

/**
 * Meses anteriores em que o mesmo evento também se aplica, além do intervalo
 * de datas — mesmo padrão dos "meses de referência" do cadastro de Cenários
 * (MetasPeriodosTab.tsx): sem limite de quantos meses, mas os pesos dos meses
 * selecionados têm que somar exatamente 100%.
 */
function MesesAnterioresTabela({ periodosAnteriores, onChange }: MesesAnterioresProps) {
  const meses = useMemo(mesesPermitidos, []);

  const alternarMes = (chave: string, marcado: boolean) => {
    const next = { ...periodosAnteriores };
    if (marcado) next[chave] = "";
    else delete next[chave];
    onChange(next);
  };

  return (
    <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">USAR</TableCell>
            <TableCell>MÊS</TableCell>
            <TableCell sx={{ minWidth: 130 }}>PESO (%)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {meses.map((chave) => {
            const selecionado = periodosAnteriores[chave] !== undefined;
            return (
              <TableRow key={chave} sx={{ bgcolor: selecionado ? "background.subtle" : "transparent" }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selecionado}
                    onChange={(ev) => alternarMes(chave, ev.target.checked)}
                    slotProps={{ input: { "aria-label": `Usar ${rotuloMesChave(chave)}` } }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {rotuloMesChave(chave)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="text"
                    inputMode="decimal"
                    disabled={!selecionado}
                    value={periodosAnteriores[chave] ?? ""}
                    placeholder="0,00"
                    onChange={(ev) => {
                      const valor = ev.target.value;
                      if (!/^\d*[.,]?\d{0,2}$/.test(valor)) return;
                      onChange({ ...periodosAnteriores, [chave]: valor });
                    }}
                    slotProps={{ htmlInput: { "aria-label": `Peso de ${rotuloMesChave(chave)} em percentual` } }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

interface FeriadoFormProps {
  values: Partial<Evento>;
  setValues: Dispatch<SetStateAction<Partial<Evento>>>;
  isEditing: boolean;
  lojas: Loja[];
  mercadologia: Mercadologia;
  erros: string[];
  onFecharErros: () => void;
}

function FeriadoForm({ values, setValues, lojas, mercadologia, erros, onFecharErros }: FeriadoFormProps) {
  const form: Rascunho = { ...RASCUNHO_VAZIO, ...values };
  const { raiz, filhos, carregandoRaiz, carregandoFilhos, indice, carregarFilhos, resolverCategorias } = mercadologia;

  const totalMeses = totalMesesAnteriores(form.periodosAnteriores);
  const dicaMeses = totalMeses.ok
    ? "Distribuição válida para gravação."
    : !totalMeses.quantidade
      ? "Selecione os meses anteriores."
      : !totalMeses.validos
        ? "Informe um peso maior que zero para cada mês selecionado."
        : totalMeses.soma < 10000
          ? `Faltam ${formatarCentesimos(10000 - totalMeses.soma)}% para completar 100%.`
          : `O total excede 100% em ${formatarCentesimos(totalMeses.soma - 10000)}%.`;

  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [filtroLojas, setFiltroLojas] = useState("");
  const [filtroCategorias, setFiltroCategorias] = useState("");

  const selecionadas = useMemo(() => new Set(form.categorias), [form.categorias]);

  // Abrir o modal em edição (ou marcar uma categoria) revela os ancestrais já
  // conhecidos — sem isso a seleção salva ficaria escondida em níveis recolhidos.
  const categoriasSelecionadas = form.categorias;
  useEffect(() => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      categoriasSelecionadas.forEach((c) => ancestraisDe(indice, c).forEach((p) => next.add(p)));
      return next;
    });
  }, [categoriasSelecionadas, indice]);

  // Um id salvo cujo nível ninguém abriu ainda não tem entrada no índice —
  // sem isso a linha ficaria com o código cru no lugar do nome. Só entra na
  // busca quem falta mesmo: uma vez indexado (aqui ou por navegação manual),
  // some da lista e o efeito não pede de novo.
  useEffect(() => {
    const desconhecidas = categoriasSelecionadas.filter((c) => !indice[c]);
    if (desconhecidas.length > 0) resolverCategorias(desconhecidas);
  }, [categoriasSelecionadas, indice, resolverCategorias]);

  // Um nível só é buscado no ERP quando alguém o abre — inclusive quando quem
  // abriu foi o efeito acima, e não um clique no expansor.
  useEffect(() => {
    expandidos.forEach((id) => {
      if (filhos[id] === undefined && !indice[id]?.folha) carregarFilhos(id);
    });
  }, [expandidos, filhos, indice, carregarFilhos]);

  const lojasVisiveis = useMemo(() => {
    const termo = normalizar(filtroLojas);
    return termo ? lojas.filter((l) => normalizar(`${l.id} ${l.nome}`).includes(termo)) : lojas;
  }, [filtroLojas, lojas]);

  // Com filtro a árvore vira uma lista achatada: cada resultado traz o caminho
  // completo, poupando o usuário de expandir nível a nível até o produto. Só os
  // níveis já carregados entram na busca — a consulta do ERP não filtra por nome.
  const categoriasEncontradas = useMemo(() => {
    const termo = normalizar(filtroCategorias);
    if (!termo) return null;
    return Object.entries(indice)
      .filter(([, info]) => normalizar(info.nome).includes(termo))
      .map(([id, info]) => ({ id, ...info }));
  }, [filtroCategorias, indice]);

  /** Ids que a árvore mostra agora: a raiz mais os filhos dos nós expandidos. */
  const idsNaArvore = useMemo(() => {
    const ids = new Set<string>();
    const percorrer = (ns: CategoriaMercadologica[]) =>
      ns.forEach((n) => {
        ids.add(n.id);
        if (expandidos.has(n.id)) percorrer(filhos[n.id] ?? []);
      });
    percorrer(raiz);
    return ids;
  }, [raiz, filhos, expandidos]);

  // Uma seleção gravada em um nível que ainda não foi aberto (ou que saiu da
  // mercadologia) não aparece na árvore: essas linhas vêm antes dela para que a
  // regra continue visível e editável.
  const selecionadasForaDaArvore = form.categorias.filter((id) => !idsNaArvore.has(id));

  const patch = (valores: Partial<Evento>) => setValues((v) => ({ ...v, ...valores }));

  const alterarRegra = (id: string, valores: Partial<Regra>) =>
    setValues((v) => {
      const regras = v.regras ?? {};
      return { ...v, regras: { ...regras, [id]: { ...(regras[id] ?? REGRA_PADRAO), ...valores } } };
    });

  const alternarExpandido = (id: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const alternarCategoria = (id: string, marcada: boolean) =>
    setValues((v) => {
      // Marcar uma categoria absorve os descendentes já marcados: a regra da
      // categoria vale para toda a subárvore.
      const atuais = v.categorias ?? [];
      const restantes = atuais.filter((c) => c !== id && !ancestraisDe(indice, c).includes(id));
      return { ...v, categorias: marcada ? [...restantes, id] : restantes };
    });

  /** Uma linha da tabela de regras. Com `caminho` a linha é um resultado de
   *  busca (ou uma seleção fora da árvore): sem recuo nem expansor, e com a
   *  trilha de ancestrais abaixo do nome. */
  const linhaCategoria = (id: string, caminho?: string) => {
    const info = indice[id];
    const pais = info?.pais ?? [];
    const nome = nomeCategoria(indice, id);
    const depth = pais.length + 1;
    const folha = info?.folha ?? false;
    const herdada = pais.some((p) => selecionadas.has(p));
    const propria = selecionadas.has(id);
    const indeterminada = !propria && !herdada && form.categorias.some((c) => ancestraisDe(indice, c).includes(id));
    const expandido = expandidos.has(id);
    const plano = caminho !== undefined;

    return (
      <TableRow key={id} sx={propria ? { bgcolor: "background.subtle" } : undefined}>
        <TableCell sx={{ minWidth: 280 }}>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ pl: plano ? 0 : `${(depth - 1) * 16}px` }}
          >
            {!plano && (
              <IconButton
                size="small"
                disabled={folha}
                onClick={() => alternarExpandido(id)}
                aria-label={`Expandir ou recolher ${nome}`}
                aria-expanded={folha ? undefined : expandido}
                sx={{ visibility: folha ? "hidden" : "visible" }}
              >
                {expandido ? <KeyboardArrowDown fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            )}
            <Box sx={{ minWidth: 0 }}>
              <FormControlLabel
                sx={{ mr: 0 }}
                label={nome}
                control={
                  <Checkbox
                    size="small"
                    checked={propria || herdada}
                    indeterminate={indeterminada}
                    disabled={herdada}
                    onChange={(ev) => alternarCategoria(id, ev.target.checked)}
                  />
                }
              />
              {plano && caminho && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: "34px" }}>
                  {caminho}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              variant="outlined"
              label={folha ? "Produto" : `N${depth}`}
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          </Stack>
        </TableCell>
        <CelulasRegra
          nome={nome}
          regra={regraDe(form, id)}
          habilitada={propria}
          herdada={herdada}
          onChange={(valores) => alterarRegra(id, valores)}
        />
      </TableRow>
    );
  };

  /** Espera pelo nível que está sendo buscado no ERP, no recuo do pai. */
  const linhaCarregando = (id: string, depth: number) => (
    <TableRow key={`carregando-${id}`}>
      <TableCell colSpan={4} sx={{ pl: `${depth * 16 + 16}px`, color: "text.secondary" }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={14} />
          <Typography variant="caption">Carregando subcategorias…</Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );

  const linhasHierarquia = (ns: CategoriaMercadologica[], depth = 1): React.ReactNode[] =>
    ns.flatMap((n) => {
      const linhas: React.ReactNode[] = [linhaCategoria(n.id)];
      if (!expandidos.has(n.id)) return linhas;
      if (filhos[n.id] === undefined || carregandoFilhos[n.id]) return [...linhas, linhaCarregando(n.id, depth)];
      return [...linhas, ...linhasHierarquia(filhos[n.id] ?? [], depth + 1)];
    });

  return (
    <>
      <Stack gap={2}>
        <TextField
          label="Nome do evento"
          required
          fullWidth
          value={form.nome}
          onChange={(e) => patch({ nome: e.target.value })}
          placeholder="Ex.: Natal, aniversário da cidade, verão"
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <TextField
            select
            label="Tipo de evento"
            fullWidth
            value={form.tipo}
            onChange={(e) => patch({ tipo: e.target.value as TipoEvento })}
          >
            <MenuItem value="Sazonalidade">Sazonalidade</MenuItem>
            <MenuItem value="Feriado">Feriado</MenuItem>
          </TextField>
          <TextField
            select
            label="Situação"
            fullWidth
            value={form.ativo ? "Ativo" : "Inativo"}
            onChange={(e) => patch({ ativo: e.target.value === "Ativo" })}
          >
            <MenuItem value="Ativo">Ativo</MenuItem>
            <MenuItem value="Inativo">Inativo</MenuItem>
          </TextField>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <TextField
            type="date"
            label="Data inicial"
            required
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            value={form.dataInicial}
            onChange={(e) => patch({ dataInicial: e.target.value })}
          />
          <TextField
            type="date"
            label="Data final"
            required
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            value={form.dataFinal}
            onChange={(e) => patch({ dataFinal: e.target.value })}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Para um feriado de um único dia, informe a mesma data nos dois campos.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.repetirAnualmente}
              onChange={(e) => patch({ repetirAnualmente: e.target.checked })}
            />
          }
          label="Repetir anualmente nas mesmas datas"
        />
      </Stack>

      <Stack gap={2}>
        <RadioGroup
          row
          value={form.abrangencia}
          onChange={(e) => patch({ abrangencia: e.target.value as Rascunho["abrangencia"] })}
        >
          <FormControlLabel value="global" control={<Radio />} label="Global · todas as lojas" />
          <FormControlLabel value="especificas" control={<Radio />} label="Lojas específicas" />
        </RadioGroup>

        {form.abrangencia === "especificas" && (
          <Box sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Filtrar lojas"
              value={filtroLojas}
              onChange={(e) => setFiltroLojas(e.target.value)}
              placeholder="Busque por código ou nome"
              sx={{ maxWidth: { sm: 360 }, mb: 1.5 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {lojasVisiveis.map((loja) => (
                <FormControlLabel
                  key={loja.id}
                  label={loja.nome}
                  sx={{
                    m: 0,
                    pr: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: form.lojas.includes(loja.id) ? "background.subtle" : "transparent",
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={form.lojas.includes(loja.id)}
                      onChange={(e) =>
                        patch({
                          lojas: e.target.checked
                            ? [...form.lojas, loja.id]
                            : form.lojas.filter((l) => l !== loja.id),
                        })
                      }
                    />
                  }
                />
              ))}
            </Stack>
            {lojasVisiveis.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {lojas.length === 0
                  ? "Nenhuma loja disponível para o seu usuário neste cliente."
                  : `Nenhuma loja encontrada para “${filtroLojas.trim()}”.`}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Selecione uma ou mais lojas da lista cadastrada. {form.lojas.length} selecionada(s).
            </Typography>
          </Box>
        )}
      </Stack>

      <Stack gap={2}>
        <RadioGroup
          row
          value={form.escopoCategorias}
          onChange={(e) => patch({ escopoCategorias: e.target.value as Rascunho["escopoCategorias"] })}
        >
          <FormControlLabel value="todas" control={<Radio />} label="Todas as categorias" />
          <FormControlLabel value="especificas" control={<Radio />} label="Selecionar na hierarquia" />
        </RadioGroup>

        {form.escopoCategorias === "especificas" && (
          <TextField
            size="small"
            fullWidth
            label="Filtrar categorias e produtos"
            value={filtroCategorias}
            onChange={(e) => setFiltroCategorias(e.target.value)}
            placeholder="Busque nos níveis já abertos"
            sx={{ maxWidth: { sm: 360 }, mt: 1.5 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          gap={1}
          sx={{ mt: 1, mb: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            {form.escopoCategorias === "todas"
              ? "Regra única para toda a árvore"
              : categoriasEncontradas
                ? `${categoriasEncontradas.length} resultado(s) · ${form.categorias.length} seleção(ões)`
                : `${form.categorias.length} seleção(ões) na hierarquia`}
          </Typography>
          <Button
            size="small"
            disabled={form.escopoCategorias === "todas" || !!categoriasEncontradas}
            onClick={() => setExpandidos(new Set())}
          >
            Recolher tudo
          </Button>
        </Stack>

        <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CATEGORIA / PRODUTO</TableCell>
                <TableCell>APLICAÇÃO NO CÁLCULO</TableCell>
                <TableCell>VARIAÇÃO (%)</TableCell>
                <TableCell>OBSERVAÇÕES</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.escopoCategorias === "todas" ? (
                <TableRow sx={{ bgcolor: "background.subtle" }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Todas as categorias e produtos
                    </Typography>
                  </TableCell>
                  <CelulasRegra
                    nome={nomeCategoria(indice, TODAS)}
                    regra={regraDe(form, TODAS)}
                    habilitada
                    herdada={false}
                    onChange={(valores) => alterarRegra(TODAS, valores)}
                  />
                </TableRow>
              ) : categoriasEncontradas ? (
                categoriasEncontradas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      Nenhuma categoria ou produto encontrado para “{filtroCategorias.trim()}”.
                    </TableCell>
                  </TableRow>
                ) : (
                  categoriasEncontradas.map((n) => linhaCategoria(n.id, caminhoDe(indice, n.pais)))
                )
              ) : carregandoRaiz ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Carregando a hierarquia do ERP…</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : raiz.length === 0 && selecionadasForaDaArvore.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    Nenhuma categoria disponível para este cliente.
                  </TableCell>
                </TableRow>
              ) : (
                [
                  ...selecionadasForaDaArvore.map((id) => linhaCategoria(id, caminhoDe(indice, ancestraisDe(indice, id)))),
                  ...linhasHierarquia(raiz),
                ]
              )}
            </TableBody>
          </Table>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Marque as categorias e configure cada linha. A regra de uma categoria vale para seus descendentes. Para
          regras diferentes, selecione as subcategorias individualmente.
        </Typography>
      </Stack>

      <Stack gap={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.usarMesesAnteriores}
              onChange={(e) => patch({ usarMesesAnteriores: e.target.checked })}
            />
          }
          label="Usar meses anteriores"
        />

        {form.usarMesesAnteriores && (
          <>
            <MesesAnterioresTabela
              periodosAnteriores={form.periodosAnteriores}
              onChange={(periodosAnteriores) => patch({ periodosAnteriores })}
            />
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={1}
              aria-live="polite"
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: totalMeses.ok ? "success.main" : "warning.main",
                bgcolor: (theme) => alpha(totalMeses.ok ? theme.palette.success.main : theme.palette.warning.main, 0.08),
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {totalMeses.quantidade} {totalMeses.quantidade === 1 ? "mês selecionado" : "meses selecionados"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dicaMeses}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total{" "}
                <Typography component="span" variant="h6" sx={{ fontWeight: 800 }}>
                  {formatarCentesimos(totalMeses.soma)}%
                </Typography>{" "}
                / 100%
              </Typography>
            </Stack>
          </>
        )}
      </Stack>

      {erros.length > 0 && (
        <Alert severity="error" onClose={onFecharErros}>
          {erros.join(" ")}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        * Campos obrigatórios
      </Typography>
    </>
  );
}

type FeriadoFiltro = {
  nome?: string;
  tipo?: string;
  situacao?: string;
  loja?: string;
};

const FILTROS_VAZIOS = { nome: "", tipo: "", situacao: "", loja: "" };

export function MetasFeriadosTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const userId = user!.id;
  const { notify } = useNotification();
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination();
  const { open, editing, handleCreate, handleEdit, handleClose } = useCrud<Evento>();

  const [eventos, setEventos] = useState<Evento[]>([]);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [erros, setErros] = useState<string[]>([]);
  const [excluindo, setExcluindo] = useState<Evento | null>(null);
  const [lojasEventoId, setLojasEventoId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Incrementada sempre que uma tentativa de resolverCategorias termina
  // (achando o nome ou não), só para forçar o recálculo de
  // nomesListagemPendentes abaixo — indice sozinho não muda quando o id
  // não é encontrado.
  const [versaoTentativas, setVersaoTentativas] = useState(0);

  const [raizCategorias, setRaizCategorias] = useState<CategoriaMercadologica[]>([]);
  const [filhosCategorias, setFilhosCategorias] = useState<Record<string, CategoriaMercadologica[]>>({});
  const [carregandoRaiz, setCarregandoRaiz] = useState(true);
  const [carregandoFilhos, setCarregandoFilhos] = useState<Record<string, boolean>>({});
  const [indice, setIndice] = useState<Indice>({});
  // Guarda contra pedir duas vezes o mesmo nível: o estado de carregamento só
  // chega no próximo render, e a árvore pode se re-renderizar antes disso.
  const buscasEmAndamento = useRef<Set<string>>(new Set());
  // Mesma guarda que buscasEmAndamento, para não repetir localizarCategorias
  // enquanto a resposta de uma chamada anterior ainda não voltou.
  const resolvendoCategorias = useRef<Set<string>>(new Set());
  // Ids para os quais já tentamos resolverCategorias e a resposta chegou
  // (achada ou não): sem isso, um id apagado do ERP nunca sai do indice
  // como pendente e o spinner da listagem trava para sempre.
  const idsTentados = useRef<Set<string>>(new Set());
  // Trocar de cliente troca a mercadologia inteira: as buscas do cliente
  // anterior que ainda estiverem em voo não podem escrever no estado novo.
  const versaoMercadologia = useRef(0);

  const [inputValues, setInputValues] = useState(FILTROS_VAZIOS);
  const [filters, setFilters] = useState<FeriadoFiltro>({});

  const eventoLojas = useMemo(
    () => eventos.find((e) => e.id === lojasEventoId) ?? null,
    [eventos, lojasEventoId]
  );

  useEffect(() => {
    if (lojasEventoId && !eventoLojas) setLojasEventoId(null);
  }, [lojasEventoId, eventoLojas]);

  useEffect(() => {
    // Sem limpar, os eventos do cliente anterior seguem na tela e clicáveis
    // até a resposta nova chegar — e para sempre, se ela falhar.
    setLoading(true);
    setEventos([]);
    listEventos(clientId)
      .then((salvos) => setEventos(salvos.map(daApi)))
      .catch(() => notify("Não foi possível carregar os eventos.", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // As lojas do evento são as empresas que o usuário logado enxerga no cliente
  // atual — o mesmo escopo que os demais módulos usam. Sem elas o cadastro
  // ainda funciona: só a abrangência por lojas específicas fica sem opções.
  useEffect(() => {
    setLojas([]);
    getUserCompanies(userId)
      .then((empresas) =>
        setLojas(
          empresas
            .filter((c) => c.client_id === clientId)
            // O código do ERP é o que o evento grava; empresa sem código ainda
            // aparece na lista, identificada pelo seu id na plataforma.
            .map((c) => ({ id: c.erp_code || c.id, nome: c.erp_code ? `${c.erp_code} · ${c.name}` : c.name }))
            .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
        ),
      )
      .catch(() => notify("Não foi possível carregar as lojas.", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, userId]);

  /** Registra os nós recém-chegados com a trilha de ancestrais do seu pai. */
  const indexar = useCallback((pai: string | null, cats: CategoriaMercadologica[]) => {
    setIndice((prev) => {
      const pais = pai === null ? [] : [...(prev[pai]?.pais ?? []), pai];
      const next = { ...prev };
      cats.forEach((c) => {
        next[c.id] = { nome: c.nome, folha: c.folha, pais };
      });
      return next;
    });
  }, []);

  // O primeiro nível da mercadologia é carregado junto da aba; os demais só
  // quando alguém abre o nó, como o comparativo do flash faz.
  useEffect(() => {
    setCarregandoRaiz(true);
    // Trocar de cliente troca a mercadologia inteira: o que já veio do cliente
    // anterior não vale mais. A raiz também precisa cair — sem isso, se a
    // carga do cliente novo falhar, a árvore volta a mostrar as categorias do
    // anterior e elas ficam selecionáveis.
    versaoMercadologia.current += 1;
    setRaizCategorias([]);
    setFilhosCategorias({});
    setIndice({});
    buscasEmAndamento.current.clear();
    resolvendoCategorias.current.clear();
    idsTentados.current.clear();
    const versao = versaoMercadologia.current;
    listCategorias(clientId)
      .then((cats) => {
        if (versao !== versaoMercadologia.current) return;
        setRaizCategorias(cats);
        indexar(null, cats);
      })
      .catch(() => {
        if (versao === versaoMercadologia.current) notify("Não foi possível carregar as categorias.", "error");
      })
      .finally(() => {
        if (versao === versaoMercadologia.current) setCarregandoRaiz(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const carregarFilhos = useCallback(
    (id: string) => {
      if (buscasEmAndamento.current.has(id)) return;
      buscasEmAndamento.current.add(id);
      setCarregandoFilhos((prev) => ({ ...prev, [id]: true }));
      const versao = versaoMercadologia.current;
      listCategorias(clientId, id)
        .then((cats) => {
          if (versao !== versaoMercadologia.current) return;
          setFilhosCategorias((prev) => ({ ...prev, [id]: cats }));
          indexar(id, cats);
        })
        .catch(() => {
          if (versao !== versaoMercadologia.current) return;
          // Sem os filhos o nó continua aberto e vazio; reabrir tenta de novo.
          buscasEmAndamento.current.delete(id);
          notify("Não foi possível carregar as subcategorias.", "error");
        })
        .finally(() => {
          if (versao === versaoMercadologia.current) setCarregandoFilhos((prev) => ({ ...prev, [id]: false }));
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientId, indexar],
  );

  // Um id de um evento salvo pode estar a alguns níveis do topo, num nível
  // que ninguém expandiu ainda — sem isso a árvore não teria como saber qual
  // caminho seguir e a linha ficaria com o código cru no lugar do nome (ver
  // comentário de localizarCategorias). Cada id vira um nó no índice, com
  // `pais` remontado a partir do codigoPai que a busca devolve.
  const resolverCategorias = useCallback(
    (ids: string[]) => {
      const pendentes = ids.filter((id) => !resolvendoCategorias.current.has(id));
      if (pendentes.length === 0) return;
      pendentes.forEach((id) => resolvendoCategorias.current.add(id));
      const versao = versaoMercadologia.current;
      localizarCategorias(clientId, pendentes)
        .then((nodos) => {
          if (versao !== versaoMercadologia.current) return;
          const porCodigo = new Map(nodos.map((n) => [n.id, n]));
          const paisDe = (id: string) => {
            const trilha: string[] = [];
            let atual = porCodigo.get(id)?.codigoPai ?? null;
            while (atual) {
              trilha.unshift(atual);
              atual = porCodigo.get(atual)?.codigoPai ?? null;
            }
            return trilha;
          };
          setIndice((prev) => {
            const next = { ...prev };
            nodos.forEach((n) => {
              next[n.id] = { nome: n.nome, folha: n.folha, pais: paisDe(n.id) };
            });
            return next;
          });
        })
        .catch(() => {
          if (versao === versaoMercadologia.current) notify("Não foi possível localizar as categorias selecionadas.", "error");
        })
        .finally(() => {
          pendentes.forEach((id) => {
            resolvendoCategorias.current.delete(id);
            idsTentados.current.add(id);
          });
          if (versao === versaoMercadologia.current) setVersaoTentativas((v) => v + 1);
        });
    },
    [clientId, notify],
  );

  const mercadologia: Mercadologia = useMemo(
    () => ({
      raiz: raizCategorias,
      filhos: filhosCategorias,
      carregandoRaiz,
      carregandoFilhos,
      indice,
      carregarFilhos,
      resolverCategorias,
    }),
    [raizCategorias, filhosCategorias, carregandoRaiz, carregandoFilhos, indice, carregarFilhos, resolverCategorias],
  );

  /** Ids de todos os eventos da listagem, independente da paginação/filtro. */
  const idsDaListagem = useMemo(() => {
    const ids = new Set<string>();
    eventos.forEach((e) => idsComRegra(e).forEach((id) => id !== TODAS && ids.add(id)));
    return ids;
  }, [eventos]);

  // Sem isso a listagem mostra o código cru até alguém abrir o cadastro: a
  // raiz carregada junto da aba só cobre o N1, e ids mais fundos (N2/produto)
  // ficam sem nome até o form da linha resolvê-los individualmente. Calculado
  // direto no render (não via effect+state) para não deixar passar um frame
  // com o código cru antes do spinner "pegar" a resolução em andamento.
  const nomesListagemPendentes = useMemo(
    () => [...idsDaListagem].some((id) => !indice[id] && !idsTentados.current.has(id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsDaListagem, indice, versaoTentativas],
  );

  useEffect(() => {
    const faltantes = [...idsDaListagem].filter((id) => !indice[id] && !idsTentados.current.has(id));
    if (faltantes.length > 0) resolverCategorias(faltantes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsDaListagem, indice, resolverCategorias, versaoTentativas]);

  const filtrados = useMemo(() => {
    const termo = normalizar(filters.nome ?? "");
    return eventos.filter((e) => {
      if (termo && !normalizar(e.nome).includes(termo)) return false;
      if (filters.tipo && e.tipo !== filters.tipo) return false;
      if (filters.situacao && e.ativo !== (filters.situacao === "ativo")) return false;
      // Um evento global vale para qualquer loja, então também casa com o filtro.
      if (filters.loja && e.abrangencia !== "global" && !e.lojas.includes(filters.loja)) return false;
      return true;
    });
  }, [eventos, filters]);

  const paginados = useMemo(
    () => filtrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtrados, page, rowsPerPage],
  );

  // Excluir os últimos registros pode deixar a página corrente vazia.
  useEffect(() => {
    if (page > 0 && paginados.length === 0) setPage(0);
  }, [page, paginados.length, setPage]);

  const abrirNovo = () => {
    setErros([]);
    handleCreate();
  };

  const abrirEdicao = (evento: Evento) => {
    setErros([]);
    handleEdit(evento);
  };

  const onSave = async (values: Partial<Evento>) => {
    const rascunho: Rascunho = { ...RASCUNHO_VAZIO, ...values };
    const problemas = validar(rascunho, indice);
    setErros(problemas);
    if (problemas.length) return;

    let salvo: Evento;
    try {
      // A resposta traz o id gerado no insert, então é ela que entra na lista.
      salvo = daApi(await saveEvento(paraApi(rascunho, editing?.id), clientId));
    } catch (err: any) {
      notify(err?.message || "Não foi possível salvar o evento.", "error");
      return;
    }

    setEventos((prev) => (editing ? prev.map((e) => (e.id === editing.id ? salvo : e)) : [...prev, salvo]));
    notify("Evento gravado.", "success");
    handleClose();
  };

  const onDeleteConfirm = async () => {
    if (!excluindo) return;
    // Fecha a confirmação antes de chamar o back: se falhar, a lista fica
    // como está e o erro chega pela notificação.
    const alvo = excluindo;
    setExcluindo(null);
    try {
      await deleteEvento(alvo.id, clientId);
    } catch (err: any) {
      notify(err?.message || "Não foi possível excluir o evento.", "error");
      return;
    }
    setEventos((prev) => prev.filter((e) => e.id !== alvo.id));
    notify("Evento excluído.", "success");
  };

  const applyFilters = () => {
    setFilters({
      nome: inputValues.nome || undefined,
      tipo: inputValues.tipo || undefined,
      situacao: inputValues.situacao || undefined,
      loja: inputValues.loja || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setInputValues(FILTROS_VAZIOS);
    setFilters({});
    setPage(0);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  if (loading || nomesListagemPendentes) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Eventos que influenciam as metas por período, loja e categoria. Cada evento gravado fica salvo na
          configuração de cálculo.
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={abrirNovo} sx={{ flexShrink: 0 }}>
          Novo cadastro
        </Button>
      </Stack>

      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Nome"
          size="small"
          fullWidth
          value={inputValues.nome}
          onChange={(e) => setInputValues((prev) => ({ ...prev, nome: e.target.value }))}
        />
        <TextField
          select
          label="Tipo"
          size="small"
          fullWidth
          value={inputValues.tipo}
          onChange={(e) => setInputValues((prev) => ({ ...prev, tipo: e.target.value }))}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="Sazonalidade">Sazonalidade</MenuItem>
          <MenuItem value="Feriado">Feriado</MenuItem>
        </TextField>
        <TextField
          select
          label="Situação"
          size="small"
          fullWidth
          value={inputValues.situacao}
          onChange={(e) => setInputValues((prev) => ({ ...prev, situacao: e.target.value }))}
        >
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="ativo">Ativo</MenuItem>
          <MenuItem value="inativo">Inativo</MenuItem>
        </TextField>
        <TextField
          select
          label="Loja"
          size="small"
          fullWidth
          value={inputValues.loja}
          onChange={(e) => setInputValues((prev) => ({ ...prev, loja: e.target.value }))}
        >
          <MenuItem value="">Todas</MenuItem>
          {lojas.map((loja) => (
            <MenuItem key={loja.id} value={loja.id}>
              {loja.nome}
            </MenuItem>
          ))}
        </TextField>
      </FilterModal>

      <DataTable
        data={paginados}
        columns={[
          { key: "ativo", label: "Ativo", render: (evento) => <Checkbox checked={evento.ativo} disabled /> },
          { key: "nome", label: "Evento" },
          { key: "tipo", label: "Tipo" },
          { key: "periodo", label: "Período", render: rotuloPeriodo },
          {
            key: "lojas",
            label: "Lojas",
            // Uma loja aparece pelo nome; várias viram um link para o modal com a lista.
            render: (evento) => {
              const nomes = evento.abrangencia === "global" ? [] : nomesLojas(evento, lojas);
              if (nomes.length <= 1)
                return (
                  <Typography variant="body2" component="span">
                    {rotuloLojas(evento, lojas)}
                  </Typography>
                );
              return (
                <Typography
                  variant="body2"
                  component="span"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLojasEventoId(evento.id);
                  }}
                  sx={{ cursor: "pointer", textDecoration: "underline" }}
                >
                  {nomes.length} lojas
                </Typography>
              );
            },
          },
          { key: "influencia", label: "Influência", render: (evento) => rotuloInfluencia(evento, indice) },
        ]}
        total={filtrados.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        actions={(evento) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton onClick={() => abrirEdicao(evento)} color="primary" aria-label={`Editar ${evento.nome}`}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => setExcluindo(evento)} color="error" aria-label={`Excluir ${evento.nome}`}>
              <Delete />
            </IconButton>
          </Stack>
        )}
      />

      {filtrados.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
          {eventos.length === 0
            ? "Nenhum evento gravado."
            : "Nenhum evento encontrado para os filtros aplicados."}
        </Typography>
      )}

      {/* Montado só enquanto aberto para que cada abertura recomece do registro atual. */}
      {open && (
        <CrudModal<Evento, Omit<FeriadoFormProps, "values" | "setValues" | "isEditing">>
          open
          onClose={handleClose}
          title={editing ? "Editar evento" : "Novo evento"}
          initialData={editing}
          onSubmit={onSave}
          Form={FeriadoForm}
          extraFormProps={{ lojas, mercadologia, erros, onFecharErros: () => setErros([]) }}
        />
      )}

      <Dialog open={!!eventoLojas} onClose={() => setLojasEventoId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Lojas de {eventoLojas?.nome}
          <Typography variant="body2" color="text.secondary">
            Abrangência do evento
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List dense disablePadding>
            {(eventoLojas ? nomesLojas(eventoLojas, lojas) : []).map((nome, index) => (
              <Box key={nome}>
                {index > 0 && <Divider component="li" />}
                <ListItem>
                  <ListItemText primary={nome} />
                </ListItem>
              </Box>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLojasEventoId(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={!!excluindo}
        onClose={() => setExcluindo(null)}
        onConfirm={onDeleteConfirm}
        title="Exclusão de evento"
        text={`Tem certeza que deseja excluir ${excluindo?.nome ?? "o registro"}?`}
        btnText="Confirmar"
      />
    </Box>
  );
}
